<template>
    <article class="tour">
        <div class="tour__main">
            <div class="tour__content">
                <PhotoGallery
                    :featured="tour.content.image"
                    :gallery="tour.content.gallery"
                    :alt-base="tour.name"
                    :label="`Photographs of ${tour.name}`"
                    priority
                    full-bleed
                    @expand="trackGalleryOpened"
                />

                <AppBreadcrumbs
                    :base-url="runtimeConfig.public.BASE_URL"
                    :parents="[
                        {
                            name: config.brand_name,
                            full_slug: '',
                        },
                        {
                            name: 'Tours',
                            full_slug: 'tours',
                        },
                    ]"
                    :current="{
                        name: tour.name,
                        full_slug: tour.full_slug,
                    }"
                />

                <h1 class="tour__title">
                    {{ tour.name }}
                </h1>

                <div class="tour__meta">
                    <div
                        v-if="locationLabel"
                        class="tour__meta-item"
                    >
                        <Icon name="tabler:map-pin" /> {{ locationLabel }}
                    </div>
                    <div
                        v-if="tour.content.duration"
                        class="tour__meta-item"
                    >
                        <Icon name="tabler:clock" /> {{ tour.content.duration }}
                    </div>
                    <div
                        v-if="fromPrice !== null"
                        class="tour__meta-item"
                    >
                        <Icon name="tabler:coin" /> from ${{ fromPrice }} pp
                    </div>
                </div>

                <div
                    v-if="tour.content.description"
                    class="tour__text"
                    v-html="renderRichText(tour.content.description)"
                />

                <section
                    v-if="includedItems.length"
                    class="tour__included"
                >
                    <h2 class="u-header-4">
                        Included in price
                    </h2>
                    <KChecklist :items="includedItems" />
                </section>

                <div
                    v-if="tour.content.faqs?.length"
                    class="tour__faqs"
                >
                    <h2 class="u-header-4">
                        Frequently asked questions
                    </h2>
                    <StoryblokComponent
                        :blok="{ component: 'faqs', items: tour.content.faqs, _uid: 'tour-faqs' }"
                    />
                </div>
            </div>

            <aside class="tour__sidebar">
                <div class="tour-calculator">
                    <h2 class="tour-calculator__title u-header-4">
                        Price calculator
                    </h2>

                    <KFieldset
                        legend="Guests"
                        class="tour-calculator__group"
                    >
                        <KField
                            v-for="cat in availableCategories"
                            :key="cat.value"
                            :label="cat.label"
                            orientation="inline"
                            class="tour-calculator__guest-row"
                        >
                            <KNumberField
                                :min="0"
                                :full-width="false"
                                :model-value="guests[cat.value]"
                                @update:model-value="value => setGuests(cat.value, value)"
                            />
                        </KField>
                    </KFieldset>

                    <p
                        v-if="content.pricing_note"
                        class="tour-calculator__pricing-note"
                    >
                        {{ content.pricing_note }}
                    </p>

                    <KField
                        v-if="hasSeasonalPricing"
                        label="Travel date"
                        class="tour-calculator__group"
                    >
                        <KDatePicker
                            calendar-label="Travel date"
                            :model-value="toCalendarDate(travelDate)"
                            @update:model-value="value => travelDate = value ? value.toString() : ''"
                        />
                    </KField>

                    <KField
                        v-if="hasTransferOptions"
                        label="Hotel location (transfer)"
                        class="tour-calculator__group"
                    >
                        <KSelect
                            v-model="hotelRegionModel"
                            placeholder="No transfer"
                            :options="transferOptions"
                        />
                    </KField>

                    <KFieldset
                        v-if="extras.length"
                        legend="Optional extras"
                        class="tour-calculator__group"
                    >
                        <KCheckbox
                            v-for="ex in extras"
                            :key="ex._uid"
                            :model-value="selectedExtras[ex._uid]"
                            @update:model-value="value => selectedExtras[ex._uid] = value === true"
                        >
                            {{ ex.label }} (+${{ Number(ex.price) }}{{ ex.unit === 'per_person' ? ' pp' : '' }})
                        </KCheckbox>
                    </KFieldset>

                    <ul class="tour-calculator__breakdown">
                        <li
                            v-for="(line, i) in breakdown"
                            :key="i"
                        >
                            <span>{{ line.label }}</span>
                            <span>{{ line.display || `$${line.amount}` }}</span>
                        </li>
                    </ul>

                    <p
                        v-if="missingPriceNote"
                        class="tour-calculator__note"
                    >
                        No price is configured for the selected group size — please contact us for a quote.
                    </p>

                    <div class="tour-calculator__total">
                        <span>Total</span>
                        <span>${{ total }}</span>
                    </div>

                    <div class="tour-calculator__actions">
                        <a
                            v-if="whatsappNumber"
                            :href="whatsappHref"
                            class="tour-calculator__whatsapp"
                            target="_blank"
                            rel="noopener"
                            @click="onWhatsappClick"
                        >
                            <Icon name="tabler:brand-whatsapp" /> Book via WhatsApp
                        </a>

                        <KButton
                            type="button"
                            class="tour-calculator__cta"
                            label="Request via email"
                            @click="openBooking"
                        />
                    </div>
                </div>
            </aside>
        </div>

        <RecommendedTours :tours="recommendedTours" />

        <BookingForm
            v-model:open="showBooking"
            :booking="bookingDetails"
        />
    </article>
</template>

<script setup>
import { parseDate } from '@internationalized/date'
import { KButton, KCheckbox, KChecklist, KDatePicker, KField, KFieldset, KNumberField, KSelect } from '@vemedia/karibu'
import { checklistItems } from '#shared/storyblok-checklist'
import { matchTourTransfers } from '#shared/tour-transfers'
import BookingForm from '~/components/booking-modal/index.vue'
import PhotoGallery from '~/components/photo-gallery/photo-gallery.vue'
import { regionLabel, useTourQuote } from '~/composables/useTourQuote'
import RecommendedTours from './recommended-tours.vue'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})

const { data: config } = await useConfig()
const { capture } = useTracking()

const { article: tour, recommendedTours } = data
const content = tour.content

const runtimeConfig = useRuntimeConfig()

const LOCATIONS = {
    safari: 'Safari',
    zanzibar: 'Zanzibar',
    moshi: 'Moshi',
    kilimanjaro: 'Kilimanjaro',
}
const pricing = content.pricing || []
const extras = content.extras || []
const transfers = content.transfer_costs || []
const includedItems = checklistItems(content.checklist_items)

// A failed dashboard request is deliberately data, not a page error: Storyblok
// transfer_costs is the migration and rollback fallback for every tour.
const { data: transferCatalog } = await useAsyncData(
    `tour-transfer-config-${tour.uuid}`,
    async () => {
        try {
            return await $fetch('/api/transfer-config')
        } catch {
            return false
        }
    },
)
const dashboardTransfer = matchTourTransfers(transferCatalog.value, {
    uuid: tour.uuid,
    fullSlug: tour.full_slug,
})

const locationLabel = computed(() => LOCATIONS[content.location] || '')

const pageUrl = `${runtimeConfig.public.BASE_URL}/${tour.full_slug.replace(/\/$/, '')}`

const {
    guests,
    travelDate,
    hotelRegion,
    selectedExtras,
    setGuests,
    availableCategories,
    hasSeasonalPricing,
    totalGuests,
    missingPriceNote,
    breakdown,
    total,
    fromPrice,
    summaryText,
    bookingDetails,
} = useTourQuote({
    pricing,
    extras,
    transfers,
    dashboardTransfer,
    hasGroupDiscount: content.has_group_discount,
    groupDiscountMinSize: content.group_discount_min_size,
    groupDiscountPercentage: content.group_discount_percentage,
    name: tour.name,
    itemUuid: tour.uuid,
    pageUrl,
})

// The quote state stores dates as the ISO `YYYY-MM-DD` string the old native input
// produced; KDatePicker models an immutable CalendarDate. An empty or malformed value
// leaves the picker blank rather than throwing during hydration.
function toCalendarDate(value) {
    if (!value)
        return undefined
    try {
        return parseDate(value)
    } catch {
        return undefined
    }
}

// KSelect's placeholder is not a selectable option, so "No transfer" is a real option
// mapped back to the composable's `''` sentinel; an unselected model must be `null`
// (reka renders `''` as placeholder-styled too, but never registers it as selected).
const NO_TRANSFER = 'none'
const hasTransferOptions = dashboardTransfer ? dashboardTransfer.pickupPlaces.length > 0 : transfers.length > 0
const transferOptions = [
    { label: 'No transfer', value: NO_TRANSFER },
    ...(dashboardTransfer
        ? dashboardTransfer.pickupPlaces.map(place => ({ label: place.name, value: String(place.id) }))
        : transfers.map(t => ({
                label: `${regionLabel(t.hotel_region)} (+$${Number(t.cost)})`,
                value: t.hotel_region,
            }))),
]
const hotelRegionModel = computed({
    get: () => hotelRegion.value || null,
    set: (value) => {
        hotelRegion.value = !value || value === NO_TRANSFER ? '' : String(value)
    },
})

// Plain-text version of the rich-text description, used as a fallback for the
// meta description and for the Product schema.
const descriptionText = computed(() =>
    content.description
        ? renderRichText(content.description).replaceAll(/<[^>]*>?/g, '').replace(/\s+/g, ' ').trim()
        : '',
)
const metaDescription = computed(
    () => content.meta_description || descriptionText.value.slice(0, 160),
)

// --- Booking flow (contact form + WhatsApp) ---
const whatsappNumber = computed(() => config.value?.phone_number || '')
const whatsappHref = computed(() =>
    `https://wa.me/${whatsappNumber.value}?text=${encodeURIComponent(summaryText.value)}`,
)

const showBooking = ref(false)

function openBooking() {
    showBooking.value = true
    capture('booking_started', { channel: 'email', placement: 'tour-booking', tour: tour.name, total: total.value, guests: totalGuests.value })
}

function onWhatsappClick() {
    capture('whatsapp_click', { placement: 'tour-booking', tour: tour.name })
    capture('booking_started', { channel: 'whatsapp', placement: 'tour-booking', tour: tour.name, total: total.value, guests: totalGuests.value })
}

let quoteTimer
watch([guests, travelDate, hotelRegion, selectedExtras], () => {
    clearTimeout(quoteTimer)
    quoteTimer = setTimeout(() => {
        capture('quote_calculated', {
            tour: tour.name,
            guests: totalGuests.value,
            hotel_region: hotelRegion.value || null,
            total: total.value,
        })
    }, 800)
})

const galleryImages = computed(() =>
    [content.image, ...(content.gallery || [])]
        .filter(img => img?.filename)
        .map(img => `${img.filename}/m/1200x630`),
)

const structuredData = computed(() => {
    const data = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': content.meta_title || tour.name,
        'description': metaDescription.value,
        'image': galleryImages.value,
        'brand': { '@type': 'Brand', 'name': config.value?.brand_name },
    }
    if (fromPrice.value !== null) {
        data.offers = {
            '@type': 'Offer',
            'price': fromPrice.value,
            'priceCurrency': 'USD',
            'availability': 'https://schema.org/InStock',
            'url': pageUrl,
        }
    }
    return JSON.stringify(data)
})

// Only ever opt *out*. `index, follow` is already the @nuxtjs/robots default, and setting
// it explicitly would override the site-wide noindex that preview/staging hosts get from
// server/plugins/site-indexable.js. `noindex, follow` and not `useRobotsRule(false)`:
// `false` resolves to the module's `robotsDisabledValue` (`noindex, nofollow`), whose
// nofollow also cuts the crawl path to any indexable children.
if (content.index === false)
    useRobotsRule('noindex, follow')

useHead({
    title: content.meta_title || tour.name,
    meta: [
        { hid: 'description', name: 'description', content: metaDescription.value },
        { property: 'og:type', content: 'product' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:title', content: content.meta_title || tour.name },
        { property: 'og:description', content: metaDescription.value },
        { property: 'og:site_name', content: config.value?.brand_name },
        ...(content.image?.filename ? [{ property: 'og:image', content: `${content.image.filename}/m/1200x630` }] : []),
        { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { hid: 'twitter:url', name: 'twitter:url', content: pageUrl },
        { hid: 'twitter:title', name: 'twitter:title', content: content.meta_title || tour.name },
        { hid: 'twitter:description', name: 'twitter:description', content: metaDescription.value },
        ...(content.image?.filename ? [{ hid: 'twitter:image', name: 'twitter:image', content: `${content.image.filename}/m/1200x630` }] : []),
    ],
    script: [
        {
            hid: 'structured-data',
            type: 'application/ld+json',
            innerHTML: structuredData.value,
        },
    ],
})

function trackGalleryOpened({ total }) {
    capture('tour_gallery_opened', {
        tour: tour.name,
        image_count: total,
    })
}

onMounted(() => {
    capture('tour_viewed', {
        tour: tour.name,
        location: content.location,
        from_price: fromPrice.value,
    })
})
</script>

<style lang="scss" scoped>
@use '@vemedia/bcn-components/scss/responsive' as *;

.tour {
    @include mobile {
        padding-inline: 16px;
    }

    &__title {
        margin-block: 1rem;
    }

    &__main {
        display: flex;
        gap: 2rem;
        align-items: flex-start;
        margin-block: 1rem;

        @include below-desktop {
            flex-direction: column;
            // Stretch children to full width in the column layout so the
            // content column isn't shrink-to-fit sized (which breaks the
            // gallery's full-bleed width calc).
            align-items: stretch;
        }
    }

    &__content {
        max-width: var(--container-2);
    }

    &__meta {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        margin-bottom: 1.5rem;

        &-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 500;

            svg {
                color: var(--primary-button-background);
            }
        }
    }

    &__text {
        line-height: 1.6;

        :deep(:is(h2:not([class]), h3, h4)) {
            margin-bottom: 8px;
        }

        :deep(:is(h2:not([class]), h3, h4):not(:first-child)) {
            margin-top: 2rem;
        }

        :deep(:is(h2:not([class]), h3, h4) + :is(h2:not(.article-head__subtitle), h3, h4)) {
            margin-top: 0;
        }

        :deep(p) {
            word-break: break-word;
        }

        :deep(p + :is(p, ul, ol)),
        :deep(:is(ul, ol) + p) {
            margin-top: 1rem;
        }

        :deep(:is(ul, ol)) {
            display: flex;
            flex-direction: column;
            row-gap: 1rem;
            margin-left: 1em;
        }
    }

    &__included {
        margin-top: 1.5rem;

        > h2 {
            margin-bottom: 8px;
        }
    }

    &__faqs {
        margin-top: 1.5rem;

        h2 {
            margin-bottom: 1rem;
        }
    }

    &__sidebar {
        flex: 1;
        min-width: 340px;

        @include below-desktop {
            width: 100%;
        }
    }
}

.tour-calculator {
    position: sticky;
    top: 1.5rem;
    padding: 1.5rem;
    border: 1px solid #dddddd;
    border-radius: 10px;
    background: #ffffff;

    &__title {
        margin-bottom: 1rem;
    }

    &__group {
        display: block;
        margin-bottom: 1rem;
    }

    &__pricing-note {
        margin: -0.5rem 0 1rem;
        font-size: 0.85rem;
        color: #666666;
    }

    // Label left, stepper right at every width — the layout the raw-HTML steppers had.
    // KField's own inline orientation only goes two-column from the tablet breakpoint up,
    // and the sidebar is narrow enough that the row always fits.
    &__guest-row {
        grid-template-columns: 1fr auto;
        align-items: center;
        margin-bottom: 0.5rem;

        :deep(.k-field__control) {
            grid-area: 1 / 2;
            margin-block-start: 0;
        }

        :deep(input) {
            max-width: 60px;
        }
    }

    &__breakdown {
        list-style: none;
        padding: 0;
        margin: 1rem 0 0.5rem;
        border-top: 1px solid #eeeeee;

        li {
            display: flex;
            justify-content: space-between;
            padding: 0.35rem 0;
            font-size: 0.9rem;
        }
    }

    &__note {
        font-size: 0.85rem;
        color: #b00020;
        margin: 0.5rem 0;
    }

    &__total {
        display: flex;
        justify-content: space-between;
        padding-top: 0.75rem;
        border-top: 2px solid #000000;
        font-weight: 700;
        font-size: 1.15rem;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-top: 1rem;
    }

    &__whatsapp {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        width: 100%;
        padding: 0.75rem;
        border-radius: 20px;
        background-color: #25d366;
        color: #ffffff;
        font-weight: 700;
        text-decoration: none;

        svg {
            font-size: 1.25rem;
        }

        &:hover {
            background-color: #1fb455;
        }
    }

    &__cta {
        display: block;
        text-align: center;
        margin-top: 0 !important;
        width: 100%;
    }
}
</style>
